import { sendGraphQLQuery } from '@/services/gql-util';

export interface TrainingStats {
	totalSamples: number;
	realCount: number;
	fakeCount: number;
	unexportedCount: number;
	lastTrainingDate: string | null;
	lastAccuracy: number | null;
}

export interface TrainingRun {
	id: number;
	vhost: number;
	categoryFilter: string | null;
	status: string;
	realCount: number;
	fakeCount: number;
	accuracy: number | null;
	bestEpoch: number | null;
	modelInfo: string | null;
	triggeredBy: number | null;
	startedAt: string | null;
	completedAt: string | null;
	errorMessage: string | null;
}

export interface MLModelInfo {
	hasCustomModel: boolean;
	models: Record<string, { accuracy?: number; [key: string]: any }> | null;
	lastTrained: string | null;
}

export function useMLTraining() {

	const getTrainingStats = async (vhostId: number, groupId?: number): Promise<TrainingStats> => {
		const query = `
			query ($vhost: Int!, $groupId: Int) {
				MLTraining {
					trainingStats(vhost: $vhost, groupId: $groupId) {
						totalSamples realCount fakeCount unexportedCount
						lastTrainingDate lastAccuracy
					}
				}
			}
		`;
		try {
			const data = await sendGraphQLQuery(query, { vhost: vhostId, groupId: groupId ?? null });
			return data?.MLTraining?.trainingStats ?? {
				totalSamples: 0, realCount: 0, fakeCount: 0,
				unexportedCount: 0, lastTrainingDate: null, lastAccuracy: null,
			};
		} catch (err) {
			console.error('Error fetching training stats:', err);
			return {
				totalSamples: 0, realCount: 0, fakeCount: 0,
				unexportedCount: 0, lastTrainingDate: null, lastAccuracy: null,
			};
		}
	};

	const getTrainingRuns = async (vhostId: number, limit: number = 20): Promise<TrainingRun[]> => {
		const query = `
			query ($vhost: Int!, $limit: Int) {
				MLTraining {
					trainingRuns(vhost: $vhost, limit: $limit) {
						id vhost categoryFilter status
						realCount fakeCount accuracy bestEpoch
						modelInfo triggeredBy startedAt completedAt errorMessage
					}
				}
			}
		`;
		try {
			const data = await sendGraphQLQuery(query, { vhost: vhostId, limit });
			return data?.MLTraining?.trainingRuns ?? [];
		} catch (err) {
			console.error('Error fetching training runs:', err);
			return [];
		}
	};

	const startTraining = async (vhostId: number, groupId?: number): Promise<{ success: boolean; message: string; runId?: number }> => {
		const mutation = `
			mutation ($vhost: Int!, $groupId: Int) {
				MLTrainingMutations {
					startTraining(vhost: $vhost, groupId: $groupId) {
						success message runId
					}
				}
			}
		`;
		try {
			const data = await sendGraphQLQuery(mutation, { vhost: vhostId, groupId: groupId ?? null });
			return data?.MLTrainingMutations?.startTraining ?? { success: false, message: 'Unknown error' };
		} catch (err: any) {
			console.error('Error starting training:', err);
			return { success: false, message: err.message || 'Failed to start training' };
		}
	};

	const getModelInfo = async (vhostId: number): Promise<MLModelInfo> => {
		const query = `
			query ($vhost: Int!) {
				MLTraining {
					modelInfo(vhost: $vhost) {
						hasCustomModel models lastTrained
					}
				}
			}
		`;
		try {
			const data = await sendGraphQLQuery(query, { vhost: vhostId });
			const info = data?.MLTraining?.modelInfo ?? { hasCustomModel: false, models: null, lastTrained: null };
			// models comes back as a JSON string from GraphQL — parse it into an object
			if (typeof info.models === 'string') {
				try {
					info.models = JSON.parse(info.models);
				} catch {
					info.models = null;
				}
			}
			return info;
		} catch (err) {
			console.error('Error fetching model info:', err);
			return { hasCustomModel: false, models: null, lastTrained: null };
		}
	};

	const checkTrainingStatus = async (vhostId: number): Promise<{ status: string; progress?: number; error?: string }> => {
		const query = `
			query ($vhost: Int!) {
				MLTraining {
					trainingStatus(vhost: $vhost) {
						status progress error
					}
				}
			}
		`;
		try {
			const data = await sendGraphQLQuery(query, { vhost: vhostId });
			return data?.MLTraining?.trainingStatus ?? { status: 'unknown' };
		} catch (err) {
			console.error('Error checking training status:', err);
			return { status: 'error', error: 'Failed to check status' };
		}
	};

	return {
		getTrainingStats,
		getTrainingRuns,
		startTraining,
		getModelInfo,
		checkTrainingStatus,
	};
}
